/**
 * billExpiry.ts — when a budget bill still counts, and when it has finished.
 *
 * In `_shared/` because the app renders the month and `kin-chat`'s budget read
 * sums it, and those two must not disagree about whether a finished bill is
 * still money going out. The app imports this by relative path, the pattern
 * `ChatMessages.tsx` already uses for `_shared/signupSheet.ts`. Nothing in
 * kin-chat reads it yet.
 *
 * ⚠️ ZERO IMPORTS, on purpose. Deno resolves this file directly, so it cannot
 * reach into `src/`. `src/lib/payDay.ts` imports `splitISODate` FROM here
 * rather than keeping its own copy, so there is one implementation of "read an
 * ISO date without a UTC shift" on both sides.
 *
 * THE RULE, and the thing it replaces: a bill counts in a viewed month when it
 * has no end date, or when its end date falls on or after the FIRST day of
 * that month. The comparison is always against the month being VIEWED, never
 * against today. The old code compared `expires_at < today`, which made the
 * answer depend on when she happened to open a month: opening November on
 * Oct 26 copied in a bill ending Nov 15, and opening it on Nov 20 did not.
 * Same bill, same month, different result.
 */

/**
 * Split an ISO date WITHOUT constructing a Date.
 *
 * ⚠️ NEVER `new Date("YYYY-MM-DD")`. That parses as UTC, so every timezone west
 * of UTC reads the day back one short — a bill ending the 1st would look like
 * it ended the previous month, and this module's whole job is that boundary.
 */
export function splitISODate(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

/** Year and month as one comparable integer, e.g. 2026-10 -> 202610. */
function monthOrdinal(parts: { y: number; m: number }): number {
  return parts.y * 12 + (parts.m - 1);
}

/**
 * Does this bill still count in `monthISO`?
 *
 * `monthISO` is a first-of-month key as `personal_budgets.month` stores it;
 * any day within the month is accepted and only its year and month are read.
 *
 * Fails OPEN. An unparseable or missing end date counts, because the
 * alternative is silently removing money from her budget on the strength of a
 * value nobody could read — and a bill that shows when it should not is a
 * visible mistake, while one that vanishes is not.
 */
export function billCountsInMonth(
  expiresAt: string | null | undefined,
  monthISO: string,
): boolean {
  if (!expiresAt) return true;
  const end = splitISODate(expiresAt);
  if (!end) return true;
  const month = splitISODate(monthISO);
  if (!month) return true;
  // On or after the first day of the viewed month. Comparing months rather
  // than days is what makes the END MONTH inclusive however late in it the
  // bill falls: ending Oct 20 still counts for all of October.
  return monthOrdinal(end) >= monthOrdinal(month);
}

/**
 * Is `monthISO` this bill's LAST month?
 *
 * Drives the "Final month" pill. Deliberately distinct from
 * `billCountsInMonth`: a bill ending in December counts in October and is not
 * final there, so one predicate cannot serve both.
 */
export function isBillFinalMonth(
  expiresAt: string | null | undefined,
  monthISO: string,
): boolean {
  if (!expiresAt) return false;
  const end = splitISODate(expiresAt);
  const month = splitISODate(monthISO);
  if (!end || !month) return false;
  return monthOrdinal(end) === monthOrdinal(month);
}

/**
 * Should this bill be copied into `targetMonthISO` when a month is carried
 * forward?
 *
 * The same question as `billCountsInMonth`, named for the call site so the
 * carry cannot drift onto a different rule. A bill must never be copied into a
 * month after its expiry month — and because rule and carry share one
 * implementation, a stale copy that an older build already created is hidden
 * by the same predicate that would have refused to write it.
 */
export function billCarriesToMonth(
  expiresAt: string | null | undefined,
  targetMonthISO: string,
): boolean {
  return billCountsInMonth(expiresAt, targetMonthISO);
}

/** The bills that count in `monthISO`, in their original order. */
export function billsCountingInMonth<B extends { expires_at?: string | null }>(
  bills: B[],
  monthISO: string,
): B[] {
  return bills.filter((b) => billCountsInMonth(b.expires_at, monthISO));
}
